import {createAction, props} from '@ngrx/store';
import {
  BelegDTO,
  EntheftenResponseDTO,
  FilterBelegDTO,
  MitarbeiterDTO,
  UpdateBelegRequestDTO,
  VorgangsartDTO,
} from '../../openapi/beleg-openapi';
import {ActivityIdentifier} from '../../interfaces/activity-identifier.interface';
import {BadgeValue} from '../../interfaces/badge-value.interface';
import {MappedHttpErrorResponse} from '@adnova/jf-ng-components';
import {PdfViewerSlot} from '../../enums/pdfviewerslot.enum';

export class BelegEntitiesActions {
  private static readonly prefix = '';

  public static startReadingBelege = createAction(this.prefix + 'Start reading list of belege.');

  public static readBelegeSuccess = createAction(
    this.prefix + 'Read list of belege successfully.',
    props<{
      belegDtos: BelegDTO[];
    }>(),
  );

  public static readBelegeFailure = createAction(
    this.prefix + 'Read list of belege failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static loadBelegByIdIfAbsent = createAction(
    this.prefix + 'Load beleg by ID if absent.',
    props<{
      inhaberId: string;
      belegId: string;
    }>(),
  );

  public static getBeleg = createAction(
    this.prefix + 'Get single beleg.',
    props<{
      inhaberId: string;
      belegId: string;
      performDubletteCheck: boolean;
    }>(),
  );

  public static getBelegSuccess = createAction(
    this.prefix + 'Get single beleg successfully.',
    props<{
      belegDto: BelegDTO;
    }>(),
  );

  public static createBeleg = createAction(
    this.prefix + 'Create beleg.',
    props<{
      inhaberId: string;
      objectUrl: string;
      size: number;
      name: string;
    }>(),
  );

  public static createBelegFailure = createAction(
    this.prefix + 'Create beleg failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static getImportStatus = createAction(
    this.prefix + 'Created beleg successfully, start getting import-state.',
    props<{
      inhaberId: string;
      belegId: string;
      belegname: string;
    }>(),
  );

  public static getImportStatusFailure = createAction(
    this.prefix + 'Getting import-state failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static openBelegPreviewDialog = createAction(
    this.prefix + 'Open preview dialog.',
    props<{
      inhaberId: string;
      belegId: string;
    }>(),
  );

  public static closePreviewDialog = createAction(this.prefix + 'Close preview dialog.');

  public static closeBelegPreviewDialog = createAction(
    this.prefix + 'Close preview dialog with cleanup.',
    props<{
      resetPdfViewerData: boolean;
      viewerSlot?: PdfViewerSlot;
    }>(),
  );

  public static deleteBeleg = createAction(
    this.prefix + 'Delete beleg.',
    props<{
      inhaberId: string;
      belegId: string;
    }>(),
  );

  public static deleteBelegSuccess = createAction(
    this.prefix + 'Delete beleg succeeded.',
    props<{
      inhaberId: string;
      belegId: string;
      identifier: ActivityIdentifier;
    }>(),
  );

  public static deleteBelegFailure = createAction(
    this.prefix + 'Delete belege failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static countBelege = createAction(
    this.prefix + 'Count belege.',
    props<{
      inhaberId: string;
      filterDto: FilterBelegDTO;
    }>(),
  );

  public static countBelegeSuccess = createAction(
    this.prefix + 'Count belege successfully.',
    props<{
      countedBelege: number;
    }>(),
  );

  public static countBadgeValue = createAction(
    this.prefix + 'Count the badge value.',
    props<{
      inhaberId: string;
    }>(),
  );

  public static countBadgeValueSuccess = createAction(
    this.prefix + 'Counted the badge value successfully.',
    props<{
      badgeValue: BadgeValue;
    }>(),
  );

  public static refreshBelegTable = createAction(
    this.prefix + 'Refresh beleg table after upload.',
    props<{
      inhaberId: string;
    }>(),
  );

  public static move = createAction(
    this.prefix + 'Start move belege action.',
    props<{
      sourceInhaberId: string;
      belege: BelegDTO[];
      destinationInhaberId: string;
    }>(),
  );

  public static moveSuccess = createAction(
    this.prefix + 'Moving beleg successfully.',
    props<{
      belegId: string;
      identifier?: ActivityIdentifier;
      sourceInhaberId: string;
      destinationInhaberId: string;
    }>(),
  );

  public static moveFailure = createAction(
    this.prefix + 'Moving beleg failed.',
    props<{
      error: MappedHttpErrorResponse;
      identifier?: ActivityIdentifier;
    }>(),
  );

  public static downloadBelegPreview = createAction(
    this.prefix + 'Download beleg preview.',
    props<{
      inhaberId: string;
      belegDto: BelegDTO;
      viewerSlot: PdfViewerSlot;
    }>(),
  );

  public static downloadBelegFile = createAction(
    this.prefix + 'Download beleg file.',
    props<{
      inhaberId: string;
      belegDto: BelegDTO;
    }>(),
  );

  public static downloadBelegFileSuccess = createAction(
    this.prefix + 'Download beleg file succeeded.',
    props<{
      objectUrl: string;
      name: string;
    }>(),
  );

  public static downloadBelegPreviewSuccess = createAction(
    this.prefix + 'Download beleg preview succeeded.',
    props<{
      objectUrl: string;
      belegDto: BelegDTO;
      viewerSlot: PdfViewerSlot;
    }>(),
  );

  public static downloadBelegFailure = createAction(
    this.prefix + 'Download belege failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static downloadBelegPreviewFailure = createAction(
    this.prefix + 'Download beleg preview failed.',
    props<{
      error: MappedHttpErrorResponse;
      viewerSlot: PdfViewerSlot;
    }>(),
  );

  public static entheften = createAction(
    this.prefix + 'Slice beleg.',
    props<{
      belegDtos: BelegDTO[];
    }>(),
  );

  public static entheftenSuccess = createAction(
    this.prefix + 'Slice beleg succeeded.',
    props<{
      inhaberId: string;
      responseDto: EntheftenResponseDTO;
      belegIdToRemove: string;
    }>(),
  );

  public static entfheftenFailure = createAction(
    this.prefix + 'Slice belege failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static markOriginalBelegAsDublette = createAction(
    this.prefix + 'Marks the original beleg as dublette.',
    props<{
      originalBelegId: string;
      dubletteId: string;
    }>(),
  );

  public static unmarkOriginalBelegAsDublette = createAction(
    this.prefix + 'Unmarks the last dublette.',
    props<{
      belegId: string;
    }>(),
  );

  public static reverseBeleg = createAction(
    this.prefix + 'Reverse beleg.',
    props<{
      belegId: string;
      inhaberId: string;
      createKorrekturBeleg: boolean;
      belegAlreadyReversed: boolean;
      isDublette: boolean;
    }>(),
  );

  public static reverseBelegSuccess = createAction(
    this.prefix + 'Reverse beleg succeeded.',
    props<{
      inhaberId: string;
      oldBelegId: string;
      korrekturBelegId: string;
      belegAlreadyReversed: boolean;
    }>(),
  );

  public static reverseBelegFailure = createAction(
    this.prefix + 'Reverse beleg failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static updateBeleg = createAction(
    this.prefix + 'Update beleg.',
    props<{
      belegDto: BelegDTO;
      updateBelegRequestDto?: UpdateBelegRequestDTO;
      stipulateBeleg?: boolean;
      prepareZahlung?: boolean;
      editNextBeleg?: boolean;
    }>(),
  );

  public static updateBelegFailure = createAction(
    this.prefix + 'Update belege failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static stipulateBelege = createAction(
    this.prefix + 'Stipulate belege.',
    props<{
      belegDtos: BelegDTO[];
    }>(),
  );

  public static stipulateBeleg = createAction(
    this.prefix + 'Stipulate beleg.',
    props<{
      inhaberId: string;
      belegId: string;
      editNextBeleg?: boolean;
    }>(),
  );

  public static stipulateBelegSuccess = createAction(this.prefix + 'Stipulate beleg succeeded.');

  public static stipulateBelegFailure = createAction(
    this.prefix + 'Stipulate belege failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static readVorgangsarten = createAction(
    this.prefix + 'Read vorgangsarten.',
    props<{
      inhaberId: string;
    }>(),
  );

  public static readVorgangsartenSuccess = createAction(
    this.prefix + 'Read vorgangsarten succeeded.',
    props<{
      vorgangsartDtos: VorgangsartDTO[];
    }>(),
  );

  public static readVorgangsartenFailure = createAction(
    this.prefix + 'Read vorgangsarten failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static readMitarbeiter = createAction(
    this.prefix + 'Read mitarbeiter.',
    props<{
      inhaberId: string;
    }>(),
  );

  public static readMitarbeiterSuccess = createAction(
    this.prefix + 'Read mitarbeiter succeeded.',
    props<{
      inhaberId: string;
      mitarbeiterDtos: MitarbeiterDTO[];
    }>(),
  );

  public static readMitarbeiterFailure = createAction(
    this.prefix + 'Read mitarbeiter failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static createMitarbeiter = createAction(
    this.prefix + 'Create mitarbeiter.',
    props<{
      inhaberId: string;
      bezeichnung: string;
    }>(),
  );

  public static createMitarbeiterSuccess = createAction(
    this.prefix + 'Create mitarbeiter succeeded.',
    props<{
      mitarbeiterDto: MitarbeiterDTO;
    }>(),
  );

  public static createMitarbeiterFailure = createAction(
    this.prefix + 'Create mitarbeiter failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static updateMitarbeiter = createAction(
    this.prefix + 'Update mitarbeiter.',
    props<{
      inhaberId: string;
      mitarbeiterId: string;
      bezeichnung: string;
      aktiv: boolean;
    }>(),
  );

  public static updateMitarbeiterSuccess = createAction(
    this.prefix + 'Update mitarbeiter succeeded.',
    props<{
      mitarbeiterDto: MitarbeiterDTO;
    }>(),
  );

  public static updateMitarbeiterFailure = createAction(
    this.prefix + 'Update mitarbeiter failed.',
    props<{
      error: MappedHttpErrorResponse;
    }>(),
  );

  public static highlightText = createAction(
    '[Preview] Highlight text.',
    props<{
      highlightText: string;
      viewerSlot: PdfViewerSlot;
    }>(),
  );
}
